/**
 * BuildMenu.ts
 * Building selection panel. Press B to toggle.
 * Shows available buildings with costs; enters placement mode on selection.
 */
import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { ResourceSystem } from '../systems/ResourceSystem';
import { BUILDING_DEFS, type BuildingDef } from '../systems/PlacementSystem';
import type { BuildingType } from '../types';

const BG_COLOUR  = 0x080b10;
const BORDER_COL = 0x1a3a30;
const ACCENT     = 0x00ffcc;
const PANEL_W    = 280;
const BTN_H      = 68;
const BTN_GAP    = 6;
const PADDING    = 14;

const S_TITLE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize: 11, fontWeight: 'bold', fill: '#c9fff8',
});
const S_DESC = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize: 9, fill: '#7aadaa',
});
const S_COST = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize: 9, fill: '#ccaa44',
});
const S_HOTKEY = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize: 9, fill: '#446655',
});

export class BuildMenu {
  private readonly container: Container;
  private readonly bg = new Graphics();
  private visible = false;
  private screenW = 800;
  private screenH = 600;
  private onSelect: (type: BuildingType) => void = () => {};

  constructor(private readonly stage: Container) {
    this.container = new Container();
    this.container.visible = false;
    stage.addChild(this.container);
    this.container.addChild(this.bg);
  }

  init(onSelect: (type: BuildingType) => void): void {
    this.onSelect = onSelect;
    this.buildPanel();
  }

  private buildPanel(): void {
    this.container.removeChildren();
    this.container.addChild(this.bg);

    const panelH = PADDING * 2 + BUILDING_DEFS.length * (BTN_H + BTN_GAP) - BTN_GAP + 28;
    const x = this.screenW - PANEL_W - 16;
    const y = this.screenH - panelH - 110; // above command panel

    // Background
    this.bg.clear();
    this.bg.lineStyle(1, BORDER_COL, 0.9);
    this.bg.beginFill(BG_COLOUR, 0.96);
    this.bg.drawRect(x, y, PANEL_W, panelH);
    this.bg.endFill();

    // Title
    const title = new Text('BUILD  [B to close]', new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize: 11, fill: '#7aadaa',
    }));
    title.position.set(x + PADDING, y + 8);
    this.container.addChild(title);

    // One button per building
    BUILDING_DEFS.forEach((def, idx) => {
      const by = y + 28 + idx * (BTN_H + BTN_GAP);

      const btn = new Graphics();
      btn.eventMode = 'static';
      btn.cursor = 'pointer';
      this.drawBtn(btn, def, x + PADDING, by, PANEL_W - PADDING * 2, BTN_H, false);

      // Hover
      btn.on('pointerover',  () => this.drawBtn(btn, def, x + PADDING, by, PANEL_W - PADDING * 2, BTN_H, true));
      btn.on('pointerout',   () => this.drawBtn(btn, def, x + PADDING, by, PANEL_W - PADDING * 2, BTN_H, false));
      btn.on('pointerdown',  () => {
        this.hide();
        this.onSelect(def.type);
      });

      // Labels
      const nameT = new Text(def.name, S_TITLE);
      nameT.position.set(x + PADDING + 10, by + 8);
      const descT = new Text(def.description, S_DESC);
      descT.position.set(x + PADDING + 10, by + 26);
      const parts: string[] = [];
      if (def.costRep   > 0) parts.push(`${def.costRep} REP`);
      if (def.costSales > 0) parts.push(`${def.costSales} SALES`);
      if (def.costGear  > 0) parts.push(`${def.costGear} GEAR`);
      const costT = new Text(parts.join('  '), S_COST);
      costT.position.set(x + PADDING + 10, by + 42);
      const hotkeyT = new Text(`${idx + 1}`, S_HOTKEY);
      hotkeyT.position.set(x + PANEL_W - PADDING - 20, by + BTN_H / 2 - 8);

      const colBox = new Graphics();
      colBox.beginFill(def.colour, 0.9);
      colBox.drawRect(x + PADDING + 1, by + BTN_H / 2 - 3, 5, 5);
      colBox.endFill();

      this.container.addChild(btn, colBox, nameT, descT, costT, hotkeyT);
    });
  }

  private drawBtn(g: Graphics, _def: BuildingDef, bx: number, by: number, bw: number, bh: number, hover: boolean): void {
    g.clear();
    g.lineStyle(1, hover ? ACCENT : BORDER_COL, hover ? 0.7 : 0.4);
    g.beginFill(hover ? 0x0d1a14 : BG_COLOUR, hover ? 0.95 : 0.7);
    g.drawRect(bx, by, bw, bh);
    g.endFill();
    // Make the interactive hit area match the drawn rect
    g.hitArea = { contains: (px: number, py: number) => px >= bx && px <= bx + bw && py >= by && py <= by + bh } as any;
  }

  update(_resources: ResourceSystem): void {
    // Could grey out buttons when unaffordable — costs are readable in the panel
  }

  toggle(): void {
    if (this.visible) this.hide(); else this.show();
  }

  show(): void {
    this.visible = true;
    this.container.visible = true;
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  get isVisible(): boolean { return this.visible; }

  resize(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    this.buildPanel();
  }
}
